import React, { useEffect, useState, useCallback } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Swal from 'sweetalert2';
import toast from 'react-hot-toast';

import { UseOutletContextCompany } from '../Company';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { InsurancePlanDA } from '../../../../services/admin/insurance-plan.service';
import { PayrollSettingsDA } from '../../../../services/admin/payroll-settings.service';
import { insurancePlanSchema, InsurancePlanFormData, INSURANCE_PLAN_TYPES, InsurancePlanType } from '../../../../schemas/insurance-plan.schema';
import { payrollSettingsSchema, PayrollSettingsFormData, PAY_FREQUENCIES, WITHHOLDING_TAX_PERIODICITIES, ADMINISTRATION_BASES } from '../../../../schemas/payroll-settings.schema';
import { configureZodErrors } from '../../../../schemas/zod-error-map';
import { dateFormat } from '../../../../scripts/date.time.format.script';
import { getCantonOptions } from '../../../../data/cantons';
import { GlobalSwal } from '../../../../styles/swal.style';

import { RHFInputText } from '../../../../components/rhf/RHFInputText';
import { RHFInputSelect } from '../../../../components/rhf/RHFInputSelect';
import { RHFInputDate } from '../../../../components/rhf/RHFInputDate';
import { RHFInputNumber } from '../../../../components/rhf/RHFInputNumber';
import { RHFInputCheckbox } from '../../../../components/rhf/RHFInputCheckbox';
import AppPreloader from '../../../../components/Preloader';

interface InsurancePlan {
  id: string;
  type: InsurancePlanType;
  valid_from: string;
  valid_to: string | null;
  insurer_name: string;
  policy_number: string | null;
  rates: { employee: number | null; employer: number | null };
  laa_risk_class: string | null;
  lpp_plan: {
    rates_by_age: { from: number; to: number; employee: number; employer: number }[];
    coordination_deduction_override: number | null;
    entry_threshold_override: number | null;
  } | null;
}

interface PayrollSettings {
  avs_fund: { name: string | null; member_number: string | null; administration_rate: number | null; administration_base: string | null };
  family_allowance_fund: { name: string | null; member_number: string | null; contribution_rate: number | null };
  withholding_tax: { canton: string | null; debtor_number: string | null; periodicity: string | null };
  pay_frequency: string;
  expense_regulation: { approved: boolean; canton: string | null; approved_date: string | null; reference: string | null };
}

const EMPTY_SETTINGS: PayrollSettings = {
  avs_fund: { name: null, member_number: null, administration_rate: null, administration_base: null },
  family_allowance_fund: { name: null, member_number: null, contribution_rate: null },
  withholding_tax: { canton: null, debtor_number: null, periodicity: null },
  pay_frequency: 'monthly',
  expense_regulation: { approved: false, canton: null, approved_date: null, reference: null },
};

type PlanStatus = 'future' | 'active' | 'closed';

function getPlanStatus(plan: { valid_from: string; valid_to: string | null }): PlanStatus {
  const today = new Date().toISOString().substring(0, 10);
  if (plan.valid_from > today) return 'future';
  if (plan.valid_to && plan.valid_to < today) return 'closed';
  return 'active';
}

const AppCompanyUpdatePayroll: React.FunctionComponent = () => {
  const { companyForUser } = UseOutletContextCompany();
  const { lang } = StateLanguage();

  const [plans, setPlans] = useState<InsurancePlan[] | null>(null);
  const [settings, setSettings] = useState<PayrollSettings | null>(null);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [loadingSettings, setLoadingSettings] = useState(true);

  // Plan form state
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState<InsurancePlan | null>(null);
  const [submittingPlan, setSubmittingPlan] = useState(false);
  const [submittingSettings, setSubmittingSettings] = useState(false);
  const [planFormError, setPlanFormError] = useState<string | null>(null);
  const [settingsFormError, setSettingsFormError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  // -----------------------------------------------------------------------
  // Load data
  // -----------------------------------------------------------------------

  const loadPlans = useCallback(async () => {
    if (!companyForUser) return;
    setLoadingPlans(true);
    try {
      const response = await InsurancePlanDA.getPlans(companyForUser.id);
      if (response.status === 200) {
        setPlans(Array.isArray(response.data) ? response.data : []);
      } else {
        setPlans([]);
      }
    } catch {
      setPlans([]);
    } finally {
      setLoadingPlans(false);
    }
  }, [companyForUser]);

  const loadSettings = useCallback(async () => {
    if (!companyForUser) return;
    setLoadingSettings(true);
    try {
      const response = await PayrollSettingsDA.getSettings(companyForUser.id);
      if (response.status === 200) {
        setSettings(response.data);
      } else if (response.status === 404) {
        // No settings yet — show empty form, no error
        setSettings(EMPTY_SETTINGS);
      } else {
        setSettings(EMPTY_SETTINGS);
      }
    } catch {
      setSettings(EMPTY_SETTINGS);
    } finally {
      setLoadingSettings(false);
    }
  }, [companyForUser]);

  useEffect(() => {
    loadPlans();
    loadSettings();
  }, [loadPlans, loadSettings]);

  // -----------------------------------------------------------------------
  // Completeness indicator
  // -----------------------------------------------------------------------

  const computeMissingItems = (): string[] => {
    const missing: string[] = [];
    if (!plans || !settings) return missing;

    // Payroll settings — mandatory configured charges (EC_AVS_ADMIN, EC_FAMILY_ALLOWANCE)
    if (!settings.avs_fund.name) {
      missing.push(lang.labels.missingAvsFundName);
    }
    if (settings.avs_fund.administration_rate == null) {
      missing.push(lang.labels.missingAvsAdminRate);
    }
    if (!settings.avs_fund.administration_base) {
      missing.push(lang.labels.missingAvsAdminBase);
    }
    if (!settings.family_allowance_fund.name) {
      missing.push(lang.labels.missingFamilyAllowanceFundName);
    }
    if (settings.family_allowance_fund.contribution_rate == null) {
      missing.push(lang.labels.missingFamilyAllowanceRate);
    }

    // Then insurance plans
    const activePlans = plans.filter(p => getPlanStatus(p) === 'active');

    const activeLaap = activePlans.find(p => p.type === 'laap');
    if (!activeLaap || activeLaap.rates.employer == null) {
      missing.push(lang.labels.missingActiveLaap);
    }

    const activeLaanp = activePlans.find(p => p.type === 'laanp');
    if (!activeLaanp || activeLaanp.rates.employee == null) {
      missing.push(lang.labels.missingActiveLaanp);
    }

    const activeLpp = activePlans.find(p => p.type === 'lpp');
    if (!activeLpp || !activeLpp.lpp_plan || activeLpp.lpp_plan.rates_by_age.length === 0) {
      missing.push(lang.labels.missingActiveLpp);
    }

    return missing;
  };

  const missingItems = computeMissingItems();
  const isComplete = missingItems.length === 0 && plans !== null && settings !== null && plans.length > 0;

  // -----------------------------------------------------------------------
  // Insurance plan form
  // -----------------------------------------------------------------------

  configureZodErrors(lang.labels);

  const planForm = useForm<InsurancePlanFormData>({
    resolver: zodResolver(insurancePlanSchema),
    defaultValues: {
      type: 'laap',
      valid_from: '',
      valid_to: '',
      insurer_name: '',
      policy_number: '',
      rates: { employee: null as any, employer: null as any },
      laa_risk_class: '',
      lpp_plan: null,
    },
  });

  const watchPlanType = planForm.watch('type');
  const isLpp = watchPlanType === 'lpp';

  // LPP age brackets field array
  const { fields: ageBrackets, append: appendBracket, remove: removeBracket } = useFieldArray({
    control: planForm.control,
    name: 'lpp_plan.rates_by_age' as any,
  });

  const openNewPlanForm = () => {
    planForm.reset({
      type: 'laap',
      valid_from: '',
      valid_to: '',
      insurer_name: '',
      policy_number: '',
      rates: { employee: null as any, employer: null as any },
      laa_risk_class: '',
      lpp_plan: null,
    });
    setPlanFormError(null);
    setEditingPlan(null);
    setShowPlanForm(true);
  };

  const openEditPlanForm = (plan: InsurancePlan) => {
    setPlanFormError(null);
    planForm.reset({
      type: plan.type,
      valid_from: plan.valid_from ? plan.valid_from.substring(0, 10) : '',
      valid_to: plan.valid_to ? plan.valid_to.substring(0, 10) : '',
      insurer_name: plan.insurer_name,
      policy_number: plan.policy_number ?? '',
      rates: {
        employee: plan.rates.employee as any,
        employer: plan.rates.employer as any,
      },
      laa_risk_class: plan.laa_risk_class ?? '',
      lpp_plan: plan.lpp_plan ?? null,
    });
    setEditingPlan(plan);
    setShowPlanForm(true);
  };

  const closePlanForm = () => {
    setShowPlanForm(false);
    setEditingPlan(null);
  };

  // Closure warning: find current active plan of same type
  const getClosureWarning = (): string | null => {
    if (editingPlan) return null; // editing, not creating
    const formType = planForm.getValues('type');
    const formDate = planForm.getValues('valid_from');
    if (!formType || !formDate || !plans) return null;

    const currentOfType = plans.find(p => p.type === formType && getPlanStatus(p) === 'active');
    if (!currentOfType) return null;

    const effectiveDate = new Date(formDate);
    const closureDate = new Date(effectiveDate.getTime() - 86400000);
    const typeLabel = lang.labels[`insurancePlanType_${formType}`] || formType;

    return (lang.labels.closurePlanWarning || '')
      .replace('{date}', dateFormat(lang.code, formDate))
      .replace('{type}', typeLabel)
      .replace('{closureDate}', dateFormat(lang.code, closureDate.toISOString().substring(0, 10)));
  };

  const handlePlanSubmit = planForm.handleSubmit(
    async (data) => {
      if (!companyForUser) return;
      setPlanFormError(null);
      setSubmittingPlan(true);

      try {
        // For LPP type, attach lpp_plan; for others, null it out
        const payload: any = { ...data };
        if (data.type !== 'lpp') {
          payload.lpp_plan = null;
        }

        let response;
        if (editingPlan) {
          response = await InsurancePlanDA.updatePlan(companyForUser.id, editingPlan.id, payload);
        } else {
          response = await InsurancePlanDA.createPlan(companyForUser.id, payload);
        }

        if (response.status === 200 || response.status === 201) {
          toast.success(lang.labels.actionSuccessful || 'Success');
          closePlanForm();
          loadPlans();
        } else {
          toast.error(response.data?.message || lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain);
        }
      } catch {
        toast.error(lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain);
      } finally {
        setSubmittingPlan(false);
      }
    },
    () => {
      setPlanFormError(lang.labels.zodFieldRequired || 'Please correct the errors above.');
    },
  );

  const handleDeletePlan = async (plan: InsurancePlan) => {
    if (!companyForUser) return;

    const result = await GlobalSwal.fire({
      title: lang.labels.confirmDeletePlan,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.deletePlan,
      cancelButtonText: lang.labels.cancel,
    });

    if (!result.isConfirmed) return;

    const response = await InsurancePlanDA.deletePlan(companyForUser.id, plan.id);
    if (response.status === 200) {
      toast.success(lang.labels.actionSuccessful || 'Success');
      loadPlans();
    } else {
      toast.error(response.data?.message || lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain);
    }
  };

  // -----------------------------------------------------------------------
  // Payroll settings form
  // -----------------------------------------------------------------------

  const settingsForm = useForm<PayrollSettingsFormData>({
    resolver: zodResolver(payrollSettingsSchema),
    defaultValues: {
      avs_fund: { name: '', member_number: '', administration_rate: null as any, administration_base: '' },
      family_allowance_fund: { name: '', member_number: '', contribution_rate: null as any },
      withholding_tax: { canton: '', debtor_number: '', periodicity: '' },
      pay_frequency: 'monthly',
      expense_regulation: { approved: false, canton: '', approved_date: '', reference: '' },
    },
  });

  const watchExpenseApproved = settingsForm.watch('expense_regulation.approved');

  // Reset settings form when data loads
  useEffect(() => {
    if (settings) {
      settingsForm.reset({
        avs_fund: {
          name: settings.avs_fund.name ?? '',
          member_number: settings.avs_fund.member_number ?? '',
          administration_rate: settings.avs_fund.administration_rate as any,
          administration_base: settings.avs_fund.administration_base ?? '',
        },
        family_allowance_fund: {
          name: settings.family_allowance_fund.name ?? '',
          member_number: settings.family_allowance_fund.member_number ?? '',
          contribution_rate: settings.family_allowance_fund.contribution_rate as any,
        },
        withholding_tax: {
          canton: settings.withholding_tax.canton ?? '',
          debtor_number: settings.withholding_tax.debtor_number ?? '',
          periodicity: settings.withholding_tax.periodicity ?? '',
        },
        pay_frequency: (settings.pay_frequency as any) || 'monthly',
        expense_regulation: {
          approved: settings.expense_regulation?.approved ?? false,
          canton: settings.expense_regulation?.canton ?? '',
          approved_date: settings.expense_regulation?.approved_date ? String(settings.expense_regulation.approved_date).substring(0, 10) : '',
          reference: settings.expense_regulation?.reference ?? '',
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  const handleSettingsSubmit = settingsForm.handleSubmit(
    async (data) => {
      if (!companyForUser) return;
      setSettingsFormError(null);
      setSubmittingSettings(true);

      try {
        // Merge with existing settings to avoid erasing fields not shown in the form
        // (e.g. accounting_mapping, debtor_account if still present server-side)
        const existing = settings ?? EMPTY_SETTINGS;
        const payload = {
          ...existing,
          ...data,
          avs_fund: { ...(existing as any).avs_fund, ...data.avs_fund },
          family_allowance_fund: { ...(existing as any).family_allowance_fund, ...data.family_allowance_fund },
          withholding_tax: { ...(existing as any).withholding_tax, ...data.withholding_tax },
          expense_regulation: { ...(existing as any).expense_regulation, ...data.expense_regulation },
        };

        const response = await PayrollSettingsDA.upsertSettings(companyForUser.id, payload);
        if (response.status === 200) {
          setSettings(response.data);
          toast.success(lang.labels.actionSuccessful || 'Success');
        } else {
          toast.error(response.data?.message || lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain);
        }
      } catch {
        toast.error(lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain);
      } finally {
        setSubmittingSettings(false);
      }
    },
    () => {
      setSettingsFormError(lang.labels.zodFieldRequired || 'Please correct the errors above.');
    },
  );

  // -----------------------------------------------------------------------
  // Helpers
  // -----------------------------------------------------------------------

  const planTypeLabel = (type: InsurancePlanType): string => {
    return lang.labels[`insurancePlanType_${type}`] || type.toUpperCase();
  };

  const canDeletePlan = (plan: InsurancePlan): boolean => {
    return getPlanStatus(plan) !== 'closed';
  };

  const cantonOptions = getCantonOptions(lang.code);

  const payFrequencyOptions = PAY_FREQUENCIES.map(f => ({
    value: f,
    label: lang.labels[`payFrequency_${f}` as keyof typeof lang.labels]
      || lang.labels[f === 'monthly' ? 'payFrequencyMonthly' : f === 'semi_monthly' ? 'payFrequencySemiMonthly' : 'payFrequencyWeekly'],
  }));

  const administrationBaseOptions = ADMINISTRATION_BASES.map(b => ({
    value: b,
    label: b === 'avs_contributions' ? lang.labels.administrationBaseAvsContributions : lang.labels.administrationBaseGrossAvs,
  }));

  const withholdingPeriodicityOptions = WITHHOLDING_TAX_PERIODICITIES.map(p => ({
    value: p,
    label: lang.labels[p === 'monthly' ? 'periodicityMonthly' : p === 'quarterly' ? 'periodicityQuarterly' : 'periodicityAnnual'],
  }));

  const planTypeOptions = INSURANCE_PLAN_TYPES.map(t => ({
    value: t,
    label: planTypeLabel(t),
  }));

  // Sort plans: by type order, then by valid_from descending
  const typeOrder = Object.fromEntries(INSURANCE_PLAN_TYPES.map((t, i) => [t, i]));
  const sortedPlans = [...(plans ?? [])].sort((a, b) => {
    const td = typeOrder[a.type] - typeOrder[b.type];
    if (td !== 0) return td;
    return new Date(b.valid_from).getTime() - new Date(a.valid_from).getTime();
  });

  const visiblePlans = showHistory
    ? sortedPlans
    : sortedPlans.filter(p => getPlanStatus(p) !== 'closed');

  const hasClosedPlans = sortedPlans.some(p => getPlanStatus(p) === 'closed');

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  if (loadingPlans || loadingSettings) {
    return <AppPreloader />;
  }

  return (
    <>
      {/* Completeness indicator */}
      <div className={`card mb-5 border ${isComplete ? 'border-success' : 'border-warning'}`}>
        <div className="card-body py-4">
          <div className="d-flex align-items-center">
            <i className={`bi ${isComplete ? 'bi-check-circle-fill text-success' : 'bi-exclamation-triangle-fill text-warning'} fs-2 me-3`}></i>
            <div>
              <h5 className="fw-bolder mb-1">{lang.labels.payrollCompleteness}</h5>
              {isComplete ? (
                <span className="text-muted fw-bold">{lang.labels.payrollCompletenessOk}</span>
              ) : (
                <>
                  <span className="text-muted fw-bold">{lang.labels.payrollCompletenessMissing}</span>
                  <ul className="mb-0 mt-2">
                    {missingItems.map((item, i) => (
                      <li key={i} className="text-muted">{item}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Payroll settings */}
      <div className="card mb-5">
        <div className="card-header">
          <div className="card-title">
            <h4 className="fw-bolder">{lang.labels.payrollSettings}</h4>
          </div>
        </div>
        <div className="card-body">
          <form onSubmit={handleSettingsSubmit}>
            {/* AVS Fund */}
            <h5 className="fw-bolder mb-5">{lang.labels.avsFund}</h5>
            <div className="row">
              <div className="col-md-4">
                <RHFInputText
                  name="avs_fund.name"
                  control={settingsForm.control}
                  label={lang.labels.avsFundName}
                />
              </div>
              <div className="col-md-4">
                <RHFInputText
                  name="avs_fund.member_number"
                  control={settingsForm.control}
                  label={lang.labels.avsMemberNumber}
                />
              </div>
              <div className="col-md-4">
                <RHFInputNumber
                  name="avs_fund.administration_rate"
                  control={settingsForm.control}
                  label={lang.labels.avsAdministrationRate}
                  suffix="%"
                  step="0.001"
                  min={0}
                  max={10}
                  required
                />
              </div>
            </div>
            <div className="row">
              <div className="col-md-4">
                <RHFInputSelect
                  name="avs_fund.administration_base"
                  control={settingsForm.control}
                  label={lang.labels.administrationBase}
                  options={administrationBaseOptions}
                  placeholder="-"
                  required
                />
              </div>
            </div>
            <div className="text-muted fs-7 mb-3">{lang.labels.administrationBaseHelp}</div>

            <div className="separator my-7"></div>

            {/* Family allowance fund */}
            <h5 className="fw-bolder mb-5">{lang.labels.familyAllowanceFund}</h5>
            <div className="row">
              <div className="col-md-4">
                <RHFInputText
                  name="family_allowance_fund.name"
                  control={settingsForm.control}
                  label={lang.labels.familyAllowanceFundName}
                />
              </div>
              <div className="col-md-4">
                <RHFInputText
                  name="family_allowance_fund.member_number"
                  control={settingsForm.control}
                  label={lang.labels.familyAllowanceMemberNumber}
                />
              </div>
              <div className="col-md-4">
                <RHFInputNumber
                  name="family_allowance_fund.contribution_rate"
                  control={settingsForm.control}
                  label={lang.labels.familyAllowanceContributionRate}
                  suffix="%"
                  step="0.001"
                  min={0}
                  max={10}
                  required
                />
              </div>
            </div>
            <div className="text-muted fs-7 mb-3">{lang.labels.familyAllowanceRateHelp}</div>

            <div className="separator my-7"></div>

            {/* Withholding tax */}
            <h5 className="fw-bolder mb-5">{lang.labels.withholdingTax}</h5>
            <div className="row">
              <div className="col-md-4">
                <RHFInputSelect
                  name="withholding_tax.canton"
                  control={settingsForm.control}
                  label={lang.labels.withholdingTaxCanton}
                  options={cantonOptions}
                  placeholder="-"
                />
              </div>
              <div className="col-md-4">
                <RHFInputText
                  name="withholding_tax.debtor_number"
                  control={settingsForm.control}
                  label={lang.labels.withholdingTaxDebtorNumber}
                />
              </div>
              <div className="col-md-4">
                <RHFInputSelect
                  name="withholding_tax.periodicity"
                  control={settingsForm.control}
                  label={lang.labels.withholdingTaxPeriodicity}
                  options={withholdingPeriodicityOptions}
                  placeholder="-"
                />
              </div>
            </div>
            <div className="text-muted fs-7 mb-3">{lang.labels.withholdingTaxNote}</div>

            <div className="separator my-7"></div>

            {/* Pay frequency */}
            <div className="row">
              <div className="col-md-4">
                <RHFInputSelect
                  name="pay_frequency"
                  control={settingsForm.control}
                  label={lang.labels.payFrequency}
                  options={payFrequencyOptions}
                  required
                />
              </div>
            </div>

            <div className="separator my-7"></div>

            {/* Expense regulation */}
            <h5 className="fw-bolder mb-5">{lang.labels.expenseRegulation}</h5>
            <RHFInputCheckbox
              name="expense_regulation.approved"
              control={settingsForm.control}
              label={lang.labels.expenseRegulationApproved}
            />
            {watchExpenseApproved && (
              <div className="row">
                <div className="col-md-4">
                  <RHFInputSelect
                    name="expense_regulation.canton"
                    control={settingsForm.control}
                    label={lang.labels.expenseRegulationCanton}
                    options={cantonOptions}
                    placeholder="-"
                  />
                </div>
                <div className="col-md-4">
                  <RHFInputDate
                    name="expense_regulation.approved_date"
                    control={settingsForm.control}
                    label={lang.labels.expenseRegulationDate}
                  />
                </div>
                <div className="col-md-4">
                  <RHFInputText
                    name="expense_regulation.reference"
                    control={settingsForm.control}
                    label={lang.labels.expenseRegulationReference}
                  />
                </div>
              </div>
            )}

            {settingsFormError && (
              <div className="alert alert-danger d-flex align-items-center mt-3">
                <i className="bi bi-exclamation-circle-fill me-2"></i>
                <span>{settingsFormError}</span>
              </div>
            )}

            <div className="d-flex justify-content-end mt-5">
              <button type="submit" className="btn btn-primary" disabled={submittingSettings}>
                {submittingSettings ? lang.labels.pleaseWait : lang.labels.save}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Insurance plans */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">
            <h4 className="fw-bolder">{lang.labels.insurancePlans}</h4>
          </div>
          <div className="card-toolbar">
            {!showPlanForm && !editingPlan && (
              <button className="btn btn-primary" onClick={openNewPlanForm}>
                <i className="bi bi-plus-lg me-1"></i>
                {lang.labels.addInsurancePlan}
              </button>
            )}
          </div>
        </div>
        <div className="card-body">
          {/* Plan form */}
          {(showPlanForm || editingPlan) && (
            <div className="card bg-light mb-5">
              <div className="card-body">
                <h5 className="fw-bolder mb-5">
                  {editingPlan ? lang.labels.editInsurancePlan : lang.labels.addInsurancePlan}
                </h5>
                <form onSubmit={handlePlanSubmit}>
                  <div className="row">
                    <div className="col-md-6">
                      <RHFInputSelect
                        name="type"
                        control={planForm.control}
                        label={lang.labels.contractType || 'Type'}
                        options={planTypeOptions}
                        required
                        disabled={!!editingPlan}
                      />
                    </div>
                    <div className="col-md-6">
                      <RHFInputText
                        name="insurer_name"
                        control={planForm.control}
                        label={lang.labels.insurerName}
                        required
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-4">
                      <RHFInputDate
                        name="valid_from"
                        control={planForm.control}
                        label={lang.labels.validFrom}
                        required
                      />
                    </div>
                    <div className="col-md-4">
                      <RHFInputDate
                        name="valid_to"
                        control={planForm.control}
                        label={lang.labels.validTo}
                      />
                    </div>
                    <div className="col-md-4">
                      <RHFInputText
                        name="policy_number"
                        control={planForm.control}
                        label={lang.labels.policyNumber}
                      />
                    </div>
                  </div>

                  {/* Rates (not for LPP) */}
                  {!isLpp && (
                    <div className="row">
                      <div className="col-md-4">
                        <RHFInputNumber
                          name="rates.employee"
                          control={planForm.control}
                          label={lang.labels.rateEmployee}
                          suffix="%"
                          step="0.001"
                          min={0}
                          max={100}
                        />
                      </div>
                      <div className="col-md-4">
                        <RHFInputNumber
                          name="rates.employer"
                          control={planForm.control}
                          label={lang.labels.rateEmployer}
                          suffix="%"
                          step="0.001"
                          min={0}
                          max={100}
                        />
                      </div>
                      {(watchPlanType === 'laap' || watchPlanType === 'laanp' || watchPlanType === 'laac') && (
                        <div className="col-md-4">
                          <RHFInputText
                            name="laa_risk_class"
                            control={planForm.control}
                            label={lang.labels.riskClass}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* LPP specific fields */}
                  {isLpp && (
                    <>
                      <h6 className="fw-bolder mt-5 mb-3">{lang.labels.lppRatesByAge}</h6>
                      {(ageBrackets as any[]).map((bracket, index) => (
                        <div className="row align-items-end" key={bracket.id}>
                          <div className="col-md-2">
                            <RHFInputNumber
                              name={`lpp_plan.rates_by_age.${index}.from` as any}
                              control={planForm.control}
                              label={lang.labels.lppAgeFrom}
                              required
                              min={17}
                              max={70}
                            />
                          </div>
                          <div className="col-md-2">
                            <RHFInputNumber
                              name={`lpp_plan.rates_by_age.${index}.to` as any}
                              control={planForm.control}
                              label={lang.labels.lppAgeTo}
                              required
                              min={18}
                              max={70}
                            />
                          </div>
                          <div className="col-md-3">
                            <RHFInputNumber
                              name={`lpp_plan.rates_by_age.${index}.employee` as any}
                              control={planForm.control}
                              label={lang.labels.rateEmployee}
                              required
                              suffix="%"
                              step="0.001"
                              min={0}
                              max={100}
                            />
                          </div>
                          <div className="col-md-3">
                            <RHFInputNumber
                              name={`lpp_plan.rates_by_age.${index}.employer` as any}
                              control={planForm.control}
                              label={lang.labels.rateEmployer}
                              required
                              suffix="%"
                              step="0.001"
                              min={0}
                              max={100}
                            />
                          </div>
                          <div className="col-md-2 mb-5">
                            <button
                              type="button"
                              className="btn btn-sm btn-light-danger"
                              onClick={() => removeBracket(index)}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="btn btn-sm btn-light-primary mb-5"
                        onClick={() => appendBracket({ from: 25, to: 34, employee: 0, employer: 0 } as any)}
                      >
                        <i className="bi bi-plus-lg me-1"></i>
                        {lang.labels.addAgeBracket}
                      </button>

                      <div className="row">
                        <div className="col-md-6">
                          <RHFInputNumber
                            name="lpp_plan.coordination_deduction_override"
                            control={planForm.control}
                            label={lang.labels.coordinationDeductionOverride}
                            step="0.01"
                            min={0}
                          />
                        </div>
                        <div className="col-md-6">
                          <RHFInputNumber
                            name="lpp_plan.entry_threshold_override"
                            control={planForm.control}
                            label={lang.labels.entryThresholdOverride}
                            step="0.01"
                            min={0}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Closure warning */}
                  {!editingPlan && (() => {
                    const warning = getClosureWarning();
                    return warning ? (
                      <div className="alert alert-warning d-flex align-items-center mt-3">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        <span>{warning}</span>
                      </div>
                    ) : null;
                  })()}

                  {planFormError && (
                    <div className="alert alert-danger d-flex align-items-center mt-3">
                      <i className="bi bi-exclamation-circle-fill me-2"></i>
                      <span>{planFormError}</span>
                    </div>
                  )}

                  <div className="d-flex justify-content-end mt-5">
                    <button type="button" className="btn btn-light me-3" onClick={closePlanForm}>
                      {lang.labels.cancel}
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={submittingPlan}>
                      {submittingPlan ? lang.labels.pleaseWait : lang.labels.save}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Plans list */}
          {plans && plans.length === 0 && !showPlanForm ? (
            <div className="text-center py-5">
              <p className="text-muted fw-bold">{lang.labels.noInsurancePlans}</p>
            </div>
          ) : visiblePlans.length > 0 ? (
            <>
              <div className="table-responsive">
                <table className="table align-middle table-row-dashed mb-2">
                  <thead className="border-bottom">
                    <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                      <th>{lang.labels.contractType || 'Type'}</th>
                      <th>{lang.labels.insurerName}</th>
                      <th>{lang.labels.validFrom}</th>
                      <th>{lang.labels.validTo}</th>
                      <th>{lang.labels.rateEmployee}</th>
                      <th>{lang.labels.rateEmployer}</th>
                      <th>{lang.labels.status}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody className="text-secondary fw-bold">
                    {visiblePlans.map(plan => {
                      const status = getPlanStatus(plan);
                      const badgeClass = status === 'active' ? 'badge-light-success'
                        : status === 'future' ? 'badge-light-info'
                        : 'badge-light-secondary';
                      const badgeLabel = status === 'active' ? lang.labels.current
                        : status === 'future' ? lang.labels.future
                        : lang.labels.closed;
                      const isLppPlan = plan.type === 'lpp';
                      return (
                        <tr key={plan.id}>
                          <td className="fw-bold">{planTypeLabel(plan.type)}</td>
                          <td>{plan.insurer_name}</td>
                          <td>{plan.valid_from ? dateFormat(lang.code, plan.valid_from) : '-'}</td>
                          <td>{plan.valid_to ? dateFormat(lang.code, plan.valid_to) : '-'}</td>
                          {isLppPlan ? (
                            <td colSpan={2}>
                              {plan.lpp_plan?.rates_by_age?.length
                                ? `${plan.lpp_plan.rates_by_age.length} ${lang.labels.lppRatesByAge.toLowerCase()}`
                                : '-'}
                            </td>
                          ) : (
                            <>
                              <td>{plan.rates.employee != null ? `${plan.rates.employee}%` : '-'}</td>
                              <td>{plan.rates.employer != null ? `${plan.rates.employer}%` : '-'}</td>
                            </>
                          )}
                          <td>
                            <span className={`badge ${badgeClass}`}>
                              {badgeLabel}
                            </span>
                          </td>
                          <td>
                            {!showPlanForm && !editingPlan && (
                              <div className="d-flex gap-1">
                                <button
                                  className="btn btn-sm btn-icon btn-light-primary"
                                  title={lang.labels.edit}
                                  onClick={() => openEditPlanForm(plan)}
                                >
                                  <i className="bi bi-pencil"></i>
                                </button>
                                {canDeletePlan(plan) && (
                                  <button
                                    className="btn btn-sm btn-icon btn-light-danger"
                                    title={lang.labels.deletePlan}
                                    onClick={() => handleDeletePlan(plan)}
                                  >
                                    <i className="bi bi-trash"></i>
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {hasClosedPlans && (
                <div className="d-flex justify-content-center mt-3">
                  <button
                    type="button"
                    className="btn btn-sm btn-light-primary"
                    onClick={() => setShowHistory(!showHistory)}
                  >
                    {showHistory ? lang.labels.hidePlanHistory : lang.labels.showPlanHistory}
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="text-center py-5">
                <p className="text-muted fw-bold">{lang.labels.noInsurancePlans}</p>
              </div>
              {hasClosedPlans && (
                <div className="d-flex justify-content-center">
                  <button
                    type="button"
                    className="btn btn-sm btn-light-primary"
                    onClick={() => setShowHistory(true)}
                  >
                    {lang.labels.showPlanHistory}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default AppCompanyUpdatePayroll;
